import type { AdminInspectorDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AvailabilityBadge, StatusBadge } from './InspectorBadges';
import { tokens } from '../../../design';
import { initials } from '../../../utils/initials';
import { shortId } from '../_lib/displayName';

/**
 * Detail-drawer header (AUT-125): avatar + name + role/status/availability
 * badges + short id + close button. Mirrors `UserDetailHeader` so the two
 * verticals look identical to ops.
 */
export const InspectorDetailHeader = ({
  inspector,
  onClose,
}: {
  inspector: AdminInspectorDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: '18px 24px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Avatar sx={{ width: 52, height: 52, fontSize: 18, fontWeight: 700 }}>
        {initials(inspector.user.name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }} noWrap>
          {inspector.user.name ?? t('inspectors.detail.noValue')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label={t('inspectors.detail.headerStatus.role')}
          />
          <StatusBadge status={inspector.status} />
          <AvailabilityBadge availability={inspector.availability} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: tokens.text.tertiary }}>
            {shortId(inspector.id)}
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label={t('inspectors.detail.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
